/**
 * Cliente HTTP da plataforma.
 *
 * Um único ponto de entrada para todas as chamadas, por três razões: o cookie
 * de sessão tem de seguir sempre (`credentials: 'include'`), o erro tem de
 * chegar à interface com a mensagem em português que o servidor escreveu, e a
 * assinatura vencida precisa de ser reconhecível para levar à renovação em vez
 * de mostrar um erro genérico.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isSubscriptionExpired(): boolean {
    return this.code === 'subscription_expired';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | null | undefined>;
}

const BASE = '/api';

export function buildUrl(
  path: string,
  query?: RequestOptions['query'],
): string {
  const url = `${BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, query } = options;

  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const texto = await response.text();
  const dados = texto ? safeJson(texto) : null;

  if (!response.ok) {
    const payload = (dados ?? {}) as {
      error?: string;
      mensagem?: string;
      message?: string;
      detalhes?: unknown;
    };
    throw new ApiError(
      response.status,
      payload.error ?? 'erro',
      payload.mensagem ?? payload.message ?? mensagemPadrao(response.status),
      payload.detalhes,
    );
  }

  return dados as T;
}

function safeJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

function mensagemPadrao(status: number): string {
  if (status === 401) return 'Sua sessão terminou. Entre novamente.';
  if (status === 403) return 'Você não tem acesso a isso.';
  if (status === 404) return 'Não encontramos o que você procura.';
  if (status === 429) return 'Muitas tentativas seguidas. Aguarde um pouco.';
  if (status >= 500) return 'Algo deu errado do nosso lado. Tente de novo.';
  return 'Não consegui completar a ação.';
}

/** Áudio sintetizado: a resposta é binária, por isso não passa por `api`. */
export async function fetchAudio(input: {
  text: string;
  gender: 'M' | 'F';
  cardId?: number;
  part?: 'front' | 'back';
}): Promise<Blob> {
  const response = await fetch(`${BASE}/tts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      'tts',
      'Não consegui gerar o áudio agora.',
    );
  }

  return response.blob();
}

/** Download binário (export ETL, etc.). */
export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename?: string }> {
  const { method = 'POST', body, signal, query } = options;

  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    const texto = await response.text();
    const dados = texto ? safeJson(texto) : null;
    const payload = (dados ?? {}) as { error?: string; mensagem?: string; message?: string };
    throw new ApiError(
      response.status,
      payload.error ?? 'erro',
      payload.mensagem ?? payload.message ?? mensagemPadrao(response.status),
    );
  }

  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename="([^"]+)"/)?.[1];
  return { blob: await response.blob(), filename };
}

/** Upload multipart para import ETL. */
export async function apiMultipart<T>(
  path: string,
  meta: unknown,
  file: File,
): Promise<T> {
  const fd = new FormData();
  fd.append('meta', JSON.stringify(meta));
  fd.append('file', file);

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });

  const texto = await response.text();
  const dados = texto ? safeJson(texto) : null;

  if (!response.ok) {
    const payload = (dados ?? {}) as { error?: string; mensagem?: string; message?: string };
    throw new ApiError(
      response.status,
      payload.error ?? 'erro',
      payload.mensagem ?? payload.message ?? mensagemPadrao(response.status),
    );
  }

  return dados as T;
}
